import {
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@material-ui/core'
import { Button } from 'components/facets'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { ConditionBadge } from 'components/facets/ConditionBadge'
import { LanguageFlag } from 'components/facets/LanguageFlag'
import { useUser } from 'components/providers/UserProvider'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { getPriceFormat } from 'lib/utils'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AggregatedInventoryArticle, CardLanguage, flagCode, generateArticleKey } from 'shared'

export interface PriceSelection {
  aggregatedStock: AggregatedInventoryArticle[]
  onConfirm: (aggregatedStock: AggregatedInventoryArticle[]) => void
}

export const PriceSelection = ({ aggregatedStock, onConfirm }: PriceSelection): JSX.Element => {
  // const { handle } = useErrorHandler()
  const { t } = useTranslation()
  const { user } = useUser()
  const { palette } = useTheme()

  const setStep = useInventoryStore((state) => state.setStep)

  const [selectedAllPriceType, setSelectedAllPriceType] = useState<string>('avg')
  const [currentAggregatedStock, setCurrentAggregatedStock] =
    useState<AggregatedInventoryArticle[]>(aggregatedStock)

  useEffect(() => {
    setCurrentAggregatedStock(aggregatedStock)
  }, [aggregatedStock])

  useEffect(() => {
    if (currentAggregatedStock.length > 0) {
      const atLeastOnePriceToSelect = currentAggregatedStock.find((article) => {
        return article.maxPrice !== article.minPrice
      })
      if (!atLeastOnePriceToSelect) {
        onConfirm(currentAggregatedStock)
      }
    }
  }, [currentAggregatedStock])

  const headerCols = [
    'card.name',
    'card.expansion',
    'card.language',
    'card.condition',
    'card.extra',
    'card.quantity',
    'ptInventory.priceSelection.minPrice',
    'ptInventory.priceSelection.avgPrice',
    'ptInventory.priceSelection.maxPrice',
    'ptInventory.priceSelection.manualSelect',
  ]

  const getApplyToAllRow = () => {
    return (
      <>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell>
              <Typography className="bodyFont">
                {t('ptInventory.priceSelection.applyToAll')}
              </Typography>
            </TableCell>
            <TableCell>
              <FormControlLabel
                data-testid="min-price-radio"
                control={<Radio checked={selectedAllPriceType === 'min'} />}
                onClick={() => selectPriceForAllArticles('min')}
                label="Min"
              />
            </TableCell>
            <TableCell>
              <FormControlLabel
                data-testid="avg-price-radio"
                control={<Radio checked={selectedAllPriceType === 'avg'} />}
                onClick={() => selectPriceForAllArticles('avg')}
                label="Avg"
              />
            </TableCell>
            <TableCell>
              <FormControlLabel
                data-testid="max-price-radio"
                control={<Radio checked={selectedAllPriceType === 'max'} />}
                onClick={() => selectPriceForAllArticles('max')}
                label="Max"
              />
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
      </>
    )
  }

  const getHeader = () => {
    return (
      <>
        {getApplyToAllRow()}
        <TableHead>
          <TableRow>
            {headerCols.map((col) => (
              <TableCell key={col}>
                <Box color={palette.primary.main}>
                  <Typography className="bodyFont">{t(col)}</Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
      </>
    )
  }

  const selectPrice = (
    article: AggregatedInventoryArticle,
    priceType: string,
    customPrice?: number
  ) => {
    if (priceType === 'min') {
      article.selectedPrice = article.minPrice
    }
    if (priceType === 'avg') {
      article.selectedPrice = article.avgPrice
    }
    if (priceType === 'max') {
      article.selectedPrice = article.maxPrice
    }
    if (priceType === 'custom' && customPrice) {
      article.selectedPrice = customPrice
    }

    const newAggregatedStock = currentAggregatedStock.map((a: AggregatedInventoryArticle) => {
      if (a.idArticle === article.idArticle) {
        a.selectedPrice = article.selectedPrice
      }
      return a
    })
    setCurrentAggregatedStock(newAggregatedStock)
  }

  const selectPriceForAllArticles = (priceType: string) => {
    setSelectedAllPriceType(priceType)
    const newAggregatedStock = currentAggregatedStock.map((a: AggregatedInventoryArticle) => {
      if (priceType === 'min') {
        a.selectedPrice = a.minPrice
      } else if (priceType === 'avg') {
        a.selectedPrice = a.avgPrice
      } else if (priceType === 'max') {
        a.selectedPrice = a.maxPrice
      }
      return a
    })
    setCurrentAggregatedStock(newAggregatedStock)
  }

  useEffect(() => {
    let arePricesAllEqual = true
    currentAggregatedStock.map((article) => {
      if (article.minPrice !== article.maxPrice) {
        arePricesAllEqual = false
        return
      }
    })
    if (arePricesAllEqual) {
      setStep(4)
    }
  }, [currentAggregatedStock])

  const getBody = () => {
    return (
      <TableBody>
        {currentAggregatedStock.map((article) => {
          const name = article.card.name
          const expansion = article.card.expansion
          const language = article.attributes[2]
          const condition = article.attributes[1]
          // const finishType = article.finishType
          // const isSigned = article.attributes[7]
          const quantity = article.quantity

          const aggregatedArticleKey = generateArticleKey(article)

          return (
            <Fragment key={article.idArticle}>
              {article.minPrice !== article.maxPrice && (
                <TableRow key={article.idArticle}>
                  <TableCell data-testid={`${aggregatedArticleKey}-name`}>{name}</TableCell>
                  <TableCell data-testid={`${aggregatedArticleKey}-expansion`}>
                    {expansion}
                  </TableCell>
                  <TableCell
                    data-testid={`${aggregatedArticleKey}-flag`}
                    style={{ textAlign: 'center' }}
                  >
                    <LanguageFlag
                      style={{ padding: '0px 5px' }}
                      height="12"
                      data-testid={`${CardLanguage[language]}-flag`}
                      code={flagCode(language) || 'gg'}
                    />
                  </TableCell>
                  <TableCell data-testid={`${aggregatedArticleKey}-condition`}>
                    <ConditionBadge condition={condition} />
                  </TableCell>
                  <TableCell data-testid={`${aggregatedArticleKey}-extras`}>
                    {/* {finishType === FINISH_TYPE_LABELS.FOIL && (
                      <img
                        alt="foil"
                        height={iconHeight}
                        src={FOIL_ICON} 
                        style={{ alignSelf: 'center', marginLeft: '2px', marginRight: '2px' }}
                      />
                    )}
                    {isSigned && (
                      <img
                        alt="signed"
                        height={iconHeight}
                        src={signedIcon}
                        style={{ alignSelf: 'center', marginLeft: '2px', marginRight: '2px' }}
                      />
                    )} */}
                    <ArticleAttributes
                      article={article}
                      iconHeight={17}
                      onlyExtraAttributes={true}
                    />
                  </TableCell>
                  <TableCell data-testid={`${aggregatedArticleKey}-quantity`}>{quantity}</TableCell>
                  <TableCell data-testid={`${aggregatedArticleKey}-minPrice`}>
                    <FormControlLabel
                      control={
                        <Radio
                          name="minPrice"
                          value="minPrice"
                          checked={article.selectedPrice === article.minPrice}
                        />
                      }
                      onClick={() => selectPrice(article, 'min')}
                      label={
                        <div
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {getPriceFormat(article.minPrice, user.currency)}
                        </div>
                      }
                    />
                  </TableCell>
                  <TableCell data-testid={`${aggregatedArticleKey}-avgPrice`}>
                    <FormControlLabel
                      control={
                        <Radio
                          name="avgPrice"
                          value="avgPrice"
                          checked={article.selectedPrice === article.avgPrice}
                        />
                      }
                      onClick={() => selectPrice(article, 'avg')}
                      label={
                        <div
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {getPriceFormat(article.avgPrice, user.currency)}
                        </div>
                      }
                    />
                  </TableCell>
                  <TableCell data-testid={`${aggregatedArticleKey}-maxPrice`}>
                    <FormControlLabel
                      control={
                        <Radio
                          name="maxPrice"
                          value="maxPrice"
                          checked={article.selectedPrice === article.maxPrice}
                        />
                      }
                      onClick={() => selectPrice(article, 'max')}
                      label={
                        <div
                          style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {getPriceFormat(article.maxPrice, user.currency)}
                        </div>
                      }
                    />
                  </TableCell>
                  <TableCell data-testid={`${aggregatedArticleKey}-selection`}>
                    <FormControl>
                      <Select
                        value={article.selectedPrice}
                        onChange={(e: any) => selectPrice(article, 'custom', e.target.value)}
                        style={{ textAlign: 'right' }}
                      >
                        {article.prices.sort().map((price: number) => (
                          <MenuItem key={price} value={price}>
                            {getPriceFormat(price, user.currency)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          )
        })}
      </TableBody>
    )
  }

  const getTable = () => {
    return (
      <TableContainer>
        <Table size="small">
          {getHeader()}
          {getBody()}
        </Table>
      </TableContainer>
    )
  }

  const handleConfirm = () => {
    onConfirm(currentAggregatedStock)
  }

  const getConfirmButton = (position: string) => {
    return (
      <Box marginTop={3} textAlign="right">
        <Button data-testid={`${position}-confirm-btn`} variant="contained" onClick={handleConfirm}>
          {t('common.confirm')}
        </Button>
      </Box>
    )
  }

  return (
    <>
      <Box className="title" marginBottom={5}>
        <Typography className="titleFont">{t('ptInventory.priceSelection.title')}</Typography>
      </Box>

      <Box marginY={2}>
        <Typography className="bodyFont">{t('ptInventory.priceSelection.text1')}</Typography>
      </Box>

      {getConfirmButton('top')}
      <Box marginTop={3} className="scrollable">
        {currentAggregatedStock.length > 0 && getTable()}
      </Box>
      {getConfirmButton('bottom')}
    </>
  )
}
